import React from 'react';
import sanitizeHtml from 'sanitize-html';
import {
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Text,
  Icon,
} from '@thebouqs/ui';
import { createBouqsCdnIcon } from '@thebouqs/icons';
import { DotwOriginalBoxProps } from './DotwOriginalBox.d';
import useTranslation from '@/hooks/useTranslation';

const CalendarIcon = createBouqsCdnIcon('calendar');

const DotwOriginalBox = ({
  data,
  dynamicDotwStrings,
  hasDiscount,
  onCheckoutClick,
}: DotwOriginalBoxProps): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Box width="xl" maxWidth="100%" px={[null, null, null, '4']} margin="auto">
      <Heading as="h2" size="xs" textAlign="center" className="dotw-heading">
        {dynamicDotwStrings.heading}
      </Heading>
      <Text textAlign="center" marginY="md" className="dotw-description">
        {dynamicDotwStrings.description}
      </Text>
      <SimpleGrid columns={{ sm: 1, lg: 2 }} spacing="6">
        <Box
          mx="auto"
          maxWidth={['56', null, null, 'none']}
          className="dotw-image">
          <img
            src={data.imageUrl}
            alt={data.imageAlt}
            width="100%"
            height="auto"
            loading="lazy"
          />
        </Box>
        <Flex
          flexDirection="column"
          alignItems={['center', null, null, 'flex-start']}
          sx={{
            p: {
              marginBottom: '2',
            },
          }}
          className="dotw-product">
          <Heading
            as="h3"
            size="md"
            marginBottom="2"
            className="dotw-product__name">
            {data.name}
          </Heading>
          <Box
            marginBottom="auto"
            fontSize="sm"
            textAlign={['center', null, null, 'inherit']}
            className="dotw-product__description"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(data.description),
            }}
          />
          <Text className="dotw-product__size">
            <Text as="span" fontWeight="bold">
              {t('common.size')}:
            </Text>
            {` ${data.size}`}
          </Text>
          <Flex alignItems="center" marginBottom="2" className="dotw-delivery">
            <Icon as={CalendarIcon} boxSize="4" marginRight="1" />
            {`${t('common.delivery')}: ${data.delivery}`}
          </Flex>
          <Box className="dotw-product__price-details">
            {hasDiscount ? (
              <>
                <Text
                  fontWeight="600"
                  size="sm"
                  textTransform="uppercase"
                  className="dotw-product__discount">
                  {dynamicDotwStrings.tag}
                </Text>
                <Text
                  as="span"
                  mr="2"
                  color="darkerGray"
                  size="sm"
                  textDecorationLine="line-through"
                  className="dotw-product__price--striked">
                  ${Math.ceil(data.price as number)}
                </Text>
              </>
            ) : null}
            <Text
              as="span"
              color={hasDiscount ? 'primary' : null}
              size="sm"
              fontWeight={hasDiscount ? null : 'semibold'}
              className="dotw-product__price">
              ${Math.ceil(data.discountedPrice as number)}
            </Text>
          </Box>
          {data.active ? (
            <Button
              variant="solid"
              colorScheme="green"
              width="full"
              marginTop={['2', null, null, 'auto']}
              className="dotw-checkout-btn"
              as={'a'}
              href={data.url}
              onClick={onCheckoutClick}>
              {t('common.checkout')}
            </Button>
          ) : null}
        </Flex>
      </SimpleGrid>
    </Box>
  );
};

export default DotwOriginalBox;
